import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { formatDateTime } from '@libs/frontend-services';
import { LuArrowRight, LuArrowLeft } from 'react-icons/lu';
import { trpc } from 'src/trpc';
import { useNavigate } from 'react-router-dom';
import { StatusDisplay } from '../../StatusDisplay';

type Props = {
  orderId: string;
};

export const RelatedOrders = ({ orderId }: Props) => {
  const navigate = useNavigate();
  const { data: relatedOrders, isLoading } = trpc.admin.order.adminGetRelatedOrders.useQuery(orderId);

  if (isLoading) {
    return null;
  }

  if (!relatedOrders || (!relatedOrders.originalOrder && relatedOrders.replacementOrders.length === 0)) {
    return null;
  }

  const handleNavigateToOrder = (orderIdToNavigate: string) => {
    navigate(`/orders/${orderIdToNavigate}`);
  };

  return (
    <div className="flex flex-col gap-4 w-full">
      <h3 className="font-semibold text-lg">Related Orders</h3>
      
      {relatedOrders.originalOrder && (
        <Card className="p-4">
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <LuArrowLeft className="w-4 h-4 text-muted" />
              <Badge variant="secondary">Original Order</Badge>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium">#{relatedOrders.originalOrder.friendlyId}</span>
                  <StatusDisplay status={relatedOrders.originalOrder.status} className="text-xs" />
                </div>
                <p className="text-sm text-muted">
                  Created {formatDateTime(relatedOrders.originalOrder.createdAt, 'MMM d, yyyy')}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleNavigateToOrder(relatedOrders.originalOrder!._id)}
              >
                View Order
              </Button>
            </div>
          </div>
        </Card>
      )}

      {relatedOrders.replacementOrders.length > 0 && (
        <>
          {relatedOrders.replacementOrders.map((replacementOrder) => (
            <Card key={replacementOrder._id} className="p-4">
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-2">
                  <LuArrowRight className="w-4 h-4 text-muted" />
                  <Badge variant="secondary">Replacement Order</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">#{replacementOrder.friendlyId}</span>
                      <StatusDisplay status={replacementOrder.status} className="text-xs" />
                    </div>
                    <p className="text-sm text-muted">
                      Created {formatDateTime(replacementOrder.createdAt, 'MMM d, yyyy')}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleNavigateToOrder(replacementOrder._id)}
                  >
                    View Order
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </>
      )}
    </div>
  );
};